import { Component, ElementRef, ViewChild, Renderer2, OnDestroy, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { SessionService } from '../../../core/service/session.service';
import { LanguageService } from '../../../core/service/language.service';
import { GlobalSearchOverlayService } from '../../service/global-search-overlay.service';

const categorySubtitles = {
  building: ['street', 'number'],
  company: ['number', 'organizationNumber'],
  contact: ['phone', 'email'],
  lease: ['tenant1Name', 'street'],
  dwelling: ['address', 'number'],
  property: ['name'],
  premises: ['street'],
  other: ['street'],
  parking: ['street'],
} as const;

type CategorySubtitles = typeof categorySubtitles;
type Category = keyof CategorySubtitles;

type Subtitle =
  | 'name'
  | 'number'
  | 'organizationNumber'
  | 'phone'
  | 'email'
  | 'address'
  | 'idString'
  | 'tenant1Name'
  | 'tenant1Email'
  | 'tenant1Phone'
  | 'tenant2Name'
  | 'tenant2Email'
  | 'tenant2Phone'
  | 'street';

//To make compatible with potential future subtitle keys from the API that are not in the predefined list, we allow any string key in the subtitle object, but only display those that are in the predefined list or match the search query. - Atlan
type SubType = 'dwellingAgreement' | 'premisesAgreement' | 'parkingLotAgreement' | 'otherAgreement' | 'blockAgreement';

type LeaseTypes = 'dwelling' | 'premises' | 'parking' | 'co-housing' | 'other';

type ResultProperties = {
  sub?: LeaseTypes | string;
} & Partial<Record<SubType, string>>;

type SearchResult = {
  id: string;
  header: string;
  title: string;
  type: Category;
  score: number;
  subtitle: Partial<Record<Subtitle, string>>;
  properties?: ResultProperties;
  path: string;
};

type ApiResult = Partial<SearchResult> & {
  debug?: { id?: string | number };
};

function sanitizeResultProperties(input: unknown): ResultProperties | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const raw = input as Record<string, unknown>;
  const clean: ResultProperties = {};

  if (typeof raw.sub === 'string' && raw.sub.trim()) {
    clean.sub = raw.sub.trim().toLowerCase();
  }

  for (const [key, value] of Object.entries(raw)) {
    if (key === 'sub') continue;
    if (typeof value !== 'string') continue;
    clean[key as SubType] = value;
  }

  return Object.keys(clean).length ? clean : undefined;
}

const allowedSubtitleKeys: readonly Subtitle[] = [
  'name',
  'number',
  'organizationNumber',
  'phone',
  'email',
  'address',
  'idString',
  'tenant1Name',
  'tenant1Email',
  'tenant1Phone',
  'tenant2Name',
  'tenant2Email',
  'tenant2Phone',
  'street',
] as const;

function isSubtitleKey(key: string): key is Subtitle {
  return (allowedSubtitleKeys as readonly string[]).includes(key);
}

function sanitizeSubtitle(input: unknown): Partial<Record<Subtitle, string>> {
  if (!input || typeof input !== 'object') return {};
  const raw = input as Record<string, unknown>;
  const clean: Partial<Record<Subtitle, string>> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!isSubtitleKey(key)) continue;
    if (typeof value !== 'string') continue;
    clean[key] = value;
  }
  return clean;
}

@Component({
    selector: 'app-global-search',
    templateUrl: './global-search.component.html',
    styleUrls: ['./global-search.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
  @Input() renderMode: 'inline' | 'dialog' = 'inline';

  @ViewChild('searchInput') inputEl?: ElementRef<HTMLInputElement>;
  @ViewChild('expandedSearchInput') expandedInputEl?: ElementRef<HTMLInputElement>;
  @ViewChild('compactSearchList') compactSearchListEl?: ElementRef<HTMLElement>;

  searchQuery = '';
  showResults = false;
  showAll = false;
  expandedAtBottom = false;

  readonly visibleLimit = 50;

  readonly categoryVisibleLimit = 5;

  readonly expandedPageSize = 50;

  expandedVisibleCount = 50;

  activeIndex: number | null = null;

  apiEndpoint = environment.apiV2 + '/search';
  apiEndpointIsActive = environment.apiV2 + '/search/is-active';
  isFeatureActive = false;

  results: SearchResult[] = [];

  compactAtBottom = false;
  compactScrollable = false;
  private readScrollState(el: HTMLElement): { atBottom: boolean; scrollable: boolean } {
    const threshold = 2;
    const scrollable = el.scrollHeight > el.clientHeight + threshold;
    const atBottom = !scrollable || el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
    return { atBottom, scrollable };
  }
  private updateCompactScrollState(el: HTMLElement | null | undefined): void {
    if (!el) {
      this.compactScrollable = false;
      this.compactAtBottom = true;
      return;
    }
    const state = this.readScrollState(el);
    this.compactScrollable = state.scrollable;
    this.compactAtBottom = state.atBottom;
  }

  onCompactListScroll(event: Event): void {
    this.updateCompactScrollState(event.target as HTMLElement);
  }

  get shouldShowCompactFooterShadow(): boolean {
    return this.compactScrollable && !this.compactAtBottom;
  }

  private refreshCompactScrollStateSoon(): void {
    setTimeout(() => this.updateCompactScrollState(this.compactSearchListEl?.nativeElement), 0);
  }

  private readonly categoryLabelKeys: Record<Category, string> = {
    contact: 'GLOBAL_SEARCH.CATEGORY.CONTACT',
    company: 'GLOBAL_SEARCH.CATEGORY.COMPANY',
    lease: 'GLOBAL_SEARCH.CATEGORY.LEASE',
    dwelling: 'GLOBAL_SEARCH.CATEGORY.DWELLING',
    property: 'GLOBAL_SEARCH.CATEGORY.PROPERTY',
    premises: 'GLOBAL_SEARCH.CATEGORY.PREMISES',
    building: 'GLOBAL_SEARCH.CATEGORY.BUILDING',
    parking: 'GLOBAL_SEARCH.CATEGORY.PARKING',
    other: 'GLOBAL_SEARCH.CATEGORY.OTHER',
  };

  getCategoryLabel(cat: Category): string {
    return this.languageService.getText(this.categoryLabelKeys[cat]);
  }

  private readonly leaseTypeLabelKeys: Record<LeaseTypes, string> = {
    dwelling: 'GLOBAL_SEARCH.LEASE_TYPE.DWELLING',
    premises: 'GLOBAL_SEARCH.LEASE_TYPE.PREMISES',
    parking: 'GLOBAL_SEARCH.LEASE_TYPE.PARKING',
    'co-housing': 'GLOBAL_SEARCH.LEASE_TYPE.COHOUSING',
    other: 'GLOBAL_SEARCH.LEASE_TYPE.OTHER',
  };

  getResultCategoryLabel(result: SearchResult): string {
    const categoryLabel = this.getCategoryLabel(result.type);
    if (result.type !== 'lease') return categoryLabel;

    const rawSub = result.properties?.sub;
    if (!rawSub) return categoryLabel;

    const key = rawSub.toLowerCase() as LeaseTypes;
    const leaseTypeKey = this.leaseTypeLabelKeys[key];
    if (!leaseTypeKey) return categoryLabel;

    const leaseTypeLabel = this.languageService.getText(leaseTypeKey);
    return leaseTypeLabel ? `${categoryLabel} - ${leaseTypeLabel}` : categoryLabel;
  }

  selectedCategory: Category | 'all' = 'all';

  isLoading = false;

  private searchDelayHandle: any = null;

  private removeEscapeListener: (() => void) | null = null;
  private isActiveAbort?: AbortController;
  private routerSubscription?: Subscription;
  private overlayOpenSubscription?: Subscription;

  constructor(
    private renderer: Renderer2,
    private sessionService: SessionService,
    private router: Router,
    private languageService: LanguageService,
    private globalSearchOverlayService: GlobalSearchOverlayService
  ) {}

  get isDialogMode(): boolean {
    return this.renderMode === 'dialog';
  }

  async ngOnInit(): Promise<void> {
    this.clearSearch();
    await this.refreshIsActive();
    if (this.isDialogMode) {
      this.overlayOpenSubscription = this.globalSearchOverlayService.openRequested$.subscribe(() => {
        this.openExpanded();
      });
    }
    this.routerSubscription = this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      if (this.isDialogMode) {
        this.clearSearch();
      }
      void this.refreshIsActive();
    });
  }

  private async refreshIsActive(): Promise<void> {
    this.isFeatureActive = await this.fetchIsActive();
    if (this.isDialogMode) {
      this.globalSearchOverlayService.setFeatureActive(this.isFeatureActive);
    }
  }

  private async fetchIsActive(): Promise<boolean> {
    return false;
    if (!this.apiEndpointIsActive) return false;

    if (this.isActiveAbort) {
      try {
        this.isActiveAbort.abort();
      } catch {}
    }

    const ctrl = new AbortController();
    this.isActiveAbort = ctrl;

    try {
      const response = await fetch(this.apiEndpointIsActive, {
        signal: ctrl.signal,
        headers: { Authorization: `Bearer ${this.sessionService.token}` },
      });

      if (!response.ok) return false;

      const data = await response.json();
      return data === true || data?.isActive === true;
    } catch (err) {
      if ((err as any)?.name === 'AbortError') return false;
      return false;
    } finally {
      if (this.isActiveAbort === ctrl) this.isActiveAbort = undefined;
    }
  }

  private get baseResults(): SearchResult[] {
    return this.results;
  }

  get categories(): Category[] {
    const order: Category[] = ['contact', 'lease', 'dwelling', 'company', 'premises', 'parking', 'building', 'property', 'other'];
    return order.filter(cat => this.baseResults.find(r => r.type === cat));
  }

  get categoryCounts(): Record<Category, number> {
    const counts: Record<Category, number> = {
      building: 0,
      contact: 0,
      lease: 0,
      dwelling: 0,
      company: 0,
      premises: 0,
      parking: 0,
      other: 0,
      property: 0,
    };
    for (const result of this.baseResults) {
      counts[result.type] += 1;
    }
    return counts;
  }

  get totalCount(): number {
    return this.baseResults.length;
  }

  get filteredResults(): SearchResult[] {
    if (this.selectedCategory === 'all') return this.baseResults;
    return this.baseResults.filter(r => r.type === this.selectedCategory);
  }

  get visibleResults(): SearchResult[] {
    const list = this.filteredResults;
    if (this.showAll) return list;
    return this.selectedCategory === 'all' ? list.slice(0, this.visibleLimit) : list.slice(0, this.categoryVisibleLimit);
  }

  get expandedVisibleResults(): SearchResult[] {
    return this.filteredResults.slice(0, this.expandedVisibleCount);
  }

  get canCollapse(): boolean {
    if (!this.showAll) return false;
    return this.selectedCategory === 'all'
      ? this.filteredResults.length > this.visibleLimit
      : this.filteredResults.length > this.categoryVisibleLimit;
  }

  setCategory(cat: Category | 'all') {
    this.selectedCategory = cat;
    this.showResults = true;
    this.activeIndex = null;
    if (this.showAll) {
      this.expandedVisibleCount = this.expandedPageSize;
    }
    this.refreshCompactScrollStateSoon();
  }

  onFocus() {
    this.showResults = !!this.searchQuery.trim();
  }
  onBlur() {
    setTimeout(() => {
      if (!this.searchQuery.trim()) {
        this.showResults = false;
        this.isLoading = false;
        if (this.searchDelayHandle) {
          clearTimeout(this.searchDelayHandle);
          this.searchDelayHandle = null;
        }
      }
    }, 150);
  }

  onSearchInput() {
    const text = (this.searchQuery || '').trim();
    const hasText = !!text;
    this.showResults = hasText;
    if (this.searchDelayHandle) {
      clearTimeout(this.searchDelayHandle);
      this.searchDelayHandle = null;
    }
    if (this.fetchAbort) {
      try {
        this.fetchAbort.abort();
      } catch {}
      this.fetchAbort = undefined;
    }
    if (!hasText) {
      this.selectedCategory = 'all';
      this.isLoading = false;

      this.results = [];
      return;
    }
    if (text.length < 3) {
      this.isLoading = false;
      this.results = [];

      return;
    }

    this.isLoading = true;
    this.searchDelayHandle = setTimeout(() => {
      this.fetchResults(text);
    }, 300);
  }

  clearSearch() {
    this.searchQuery = '';
    this.showAll = false;
    this.showResults = false;
    this.selectedCategory = 'all';
    this.isLoading = false;
    this.refreshCompactScrollStateSoon();
    if (this.searchDelayHandle) {
      clearTimeout(this.searchDelayHandle);
      this.searchDelayHandle = null;
    }
    if (this.fetchAbort) {
      try {
        this.fetchAbort.abort();
      } catch {}
      this.fetchAbort = undefined;
    }

    this.results = [];
  }

  closeResults() {
    this.showResults = false;
    this.showAll = false;
    this.activeIndex = null;
    if (!this.isDialogMode) {
      setTimeout(() => this.inputEl?.nativeElement.focus(), 0);
    }
    if (this.removeEscapeListener) {
      this.removeEscapeListener();
      this.removeEscapeListener = null;
    }
  }

  onEscape() {
    this.clearSearch();
    this.closeResults();
  }

  openExpanded() {
    if (!this.isFeatureActive) return;

    this.clearSearch();
    this.showAll = true;
    this.showResults = true;
    this.activeIndex = null;

    if (!this.removeEscapeListener) {
      this.removeEscapeListener = this.renderer.listen('document', 'keydown', (event: KeyboardEvent) => {
        if (event.key === 'Escape' && this.showAll) {
          event.preventDefault();
          this.onEscape();
        }
      });
    }

    setTimeout(() => this.expandedInputEl?.nativeElement.focus(), 0);
  }

  showAllResults() {
    this.expandedVisibleCount = this.expandedPageSize;

    if (!this.removeEscapeListener) {
      this.removeEscapeListener = this.renderer.listen('document', 'keydown', (event: KeyboardEvent) => {
        if (event.key === 'Escape' && this.showResults && this.showAll) {
          event.preventDefault();
          this.onEscape();
        }
      });
    }
    this.showAll = true;
  }

  collapseResults() {
    this.showAll = false;
    this.refreshCompactScrollStateSoon();
    if (this.removeEscapeListener) {
      this.removeEscapeListener();
      this.removeEscapeListener = null;
    }
  }

  onResultClick(result: SearchResult) {
    if (result.path) {
      // Normalize certain paths (e.g. strip "_lease" suffix from dwelling-contract ids)
      const target = this.normalizeLinkPath(result.path);

      this.closeResults();
      this.router.navigateByUrl(target);
    }
  }

  // Normalize known link patterns coming from search API before navigating
  // Strip any "_<suffix>" after a 32-char hex id in a path segment
  // - Example: "/api/v1/dwelling-contract/5514..._lease" -> "/api/v1/dwelling-contract/5514..."
  // - Example: "/api/v1/contact/5514..._contact" -> "/api/v1/contact/5514..."
  private normalizeLinkPath(path: string): string {
    if (!path) return path;
    // Strip suffixes from any segment that looks like "{32-hex}_{something}"
    return path.replace(/(\/)([0-9a-fA-F]{32})_[^\/?#]*/g, '$1$2');
  }

  formatCount(count: number): string {
    return count > 99 ? '99+' : String(count);
  }
  loadMoreExpanded(): void {
    const next = this.expandedVisibleCount + this.expandedPageSize;
    this.expandedVisibleCount = Math.min(next, this.filteredResults.length);
  }

  trackById(_: number, result: SearchResult): string {
    return result.id;
  }

  getItemClass(cat: Category): string {
    return 'global-search__search-result-item--' + cat;
  }

  isActive(result: SearchResult): boolean {
    if (this.activeIndex == null) return false;
    const active = this.navigableResults[this.activeIndex];
    return !!active && active.id === result.id;
  }

  // This method appends "wild card" that match the search query, outside of the predefined subtitle keys for the category. We have some cases of "wild card" data being appended despite being part of the header/subtitle, also some cases of it seemingly not being appended, so we may want to examine this eventually. - Atlan

  private getVisibleSubtitleKeys(result: SearchResult): Subtitle[] {
    const baseKeys = categorySubtitles[result.type] as readonly Subtitle[];
    const keys: Subtitle[] = [...baseKeys];
    const seen = new Set<Subtitle>(baseKeys);

    const query = (this.searchQuery || '').trim().toLowerCase();

    for (const [key, value] of Object.entries(result.subtitle) as Array<[Subtitle, string | undefined]>) {
      if (seen.has(key)) continue;
      if (!value || !value.trim()) continue;
      if (query && !value.toLowerCase().includes(query)) continue;
      keys.push(key);
      seen.add(key);
    }

    return keys;
  }

  getSubtitleRows(result: SearchResult): Array<{ key: Subtitle; value: string }> {
    return this.getVisibleSubtitleKeys(result).map(key => ({
      key,
      value: result.subtitle[key] ?? '-',
    }));
  }

  private readonly subtitleLabelKeys: Record<Subtitle, string> = {
    name: 'GLOBAL_SEARCH.SUBTITLE.NAME',
    number: 'GLOBAL_SEARCH.SUBTITLE.NUMBER',
    organizationNumber: 'GLOBAL_SEARCH.SUBTITLE.ORGANIZATION_NUMBER',
    phone: 'GLOBAL_SEARCH.SUBTITLE.PHONE',
    email: 'GLOBAL_SEARCH.SUBTITLE.EMAIL',
    address: 'GLOBAL_SEARCH.SUBTITLE.ADDRESS',
    idString: 'GLOBAL_SEARCH.SUBTITLE.ID_STRING',
    tenant1Name: 'GLOBAL_SEARCH.SUBTITLE.TENANT1_NAME',
    tenant1Email: 'GLOBAL_SEARCH.SUBTITLE.TENANT1_EMAIL',
    tenant1Phone: 'GLOBAL_SEARCH.SUBTITLE.TENANT1_PHONE',
    tenant2Name: 'GLOBAL_SEARCH.SUBTITLE.TENANT2_NAME',
    tenant2Email: 'GLOBAL_SEARCH.SUBTITLE.TENANT2_EMAIL',
    tenant2Phone: 'GLOBAL_SEARCH.SUBTITLE.TENANT2_PHONE',
    street: 'GLOBAL_SEARCH.SUBTITLE.STREET',
  };

  private readonly categoryNumberLabelKeys: Record<Category, string> = {
    dwelling: 'GLOBAL_SEARCH.SUBTITLE.RENTAL_ID',
    other: 'GLOBAL_SEARCH.SUBTITLE.RENTAL_ID',
    parking: 'GLOBAL_SEARCH.SUBTITLE.RENTAL_ID',
    premises: 'GLOBAL_SEARCH.SUBTITLE.RENTAL_ID',
    property: 'GLOBAL_SEARCH.SUBTITLE.PROPERTY_NUMBER',
    building: this.subtitleLabelKeys.number,
    company: this.subtitleLabelKeys.number,
    contact: 'GLOBAL_SEARCH.SUBTITLE.CONTACT_NUMBER',
    lease: 'GLOBAL_SEARCH.SUBTITLE.LEASE_NUMBER',
  };

  getSubtitleLabel(key: Subtitle, category?: Category): string {
    const categoryKey = key === 'number' && category ? this.categoryNumberLabelKeys[category] : undefined;
    const translationKey = categoryKey ?? this.subtitleLabelKeys[key];
    const text = this.languageService.getText(translationKey);
    return text || key;
  }

  getOptionId(result: SearchResult): string {
    return 'global-search-option-' + result.id;
  }

  get navigableResults(): SearchResult[] {
    if (!this.showResults) return [];
    return this.visibleResults;
  }

  onKeyDown(event: KeyboardEvent) {
    const list = this.navigableResults;
    if (!list.length) return;
    const key = event.key;
    if (key === 'ArrowDown') {
      event.preventDefault();
      if (this.activeIndex == null) this.activeIndex = 0;
      else this.activeIndex = Math.min(this.activeIndex + 1, list.length - 1);
      this.scrollActiveIntoView();
    } else if (key === 'ArrowUp') {
      event.preventDefault();
      if (this.activeIndex == null) this.activeIndex = list.length - 1;
      else this.activeIndex = Math.max(this.activeIndex - 1, 0);
      this.scrollActiveIntoView();
    } else if (key === 'Enter') {
      if (this.activeIndex != null) {
        event.preventDefault();
        const r = list[this.activeIndex];
        if (r) this.onResultClick(r);
      }
    }
  }

  get activeOptionId(): string | null {
    if (this.activeIndex == null) return null;
    const r = this.navigableResults[this.activeIndex];
    return r ? this.getOptionId(r) : null;
  }

  private scrollActiveIntoView() {
    const id = this.activeOptionId;
    if (!id) return;
    const el = document.getElementById(id);
    if (el && typeof el.scrollIntoView === 'function') {
      try {
        el.scrollIntoView({ block: 'nearest' });
      } catch {
        el.scrollIntoView();
      }
    }
  }

  ngOnDestroy() {
    this.overlayOpenSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();

    if (this.isDialogMode) {
      this.globalSearchOverlayService.setFeatureActive(false);
    }

    if (this.isActiveAbort) {
      try {
        this.isActiveAbort.abort();
      } catch {}
      this.isActiveAbort = undefined;
    }

    if (this.removeEscapeListener) {
      this.removeEscapeListener();
      this.removeEscapeListener = null;
    }

    this.clearSearch();
  }

  get shouldShowExpandedFooterShadow(): boolean {
    if (this.expandedVisibleCount < this.filteredResults.length) return true;
    return !this.expandedAtBottom;
  }

  onExpandedOverlayScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const threshold = 2;
    this.expandedAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
  }

  private fetchAbort?: AbortController;

  private extractItems(data: unknown): unknown[] {
    if (Array.isArray(data)) return data;
    if (!data || typeof data !== 'object') return [];
    const obj = data as { items?: unknown; results?: unknown };
    if (Array.isArray(obj.items)) return obj.items;
    if (Array.isArray(obj.results)) return obj.results;
    return [];
  }

  private buildQueryUrl(text: string): string {
    const sep = this.apiEndpoint.includes('?') ? '&' : '?';
    return `${this.apiEndpoint}${sep}query=${encodeURIComponent(text)}`;
  }

  private buildStableId(input: { type: Category; path: string; title: string }): string {
    const raw = `${input.type}|${input.path}|${input.title}`;
    return raw.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  }

  private toResults(data: unknown): SearchResult[] {
    const items = this.extractItems(data) as ApiResult[];

    const mapped = items.map(item => {
      const type: Category =
        item.type === 'contact' ||
        item.type === 'lease' ||
        item.type === 'dwelling' ||
        item.type === 'company' ||
        item.type === 'premises' ||
        item.type === 'parking' ||
        item.type === 'building' ||
        item.type === 'property' ||
        item.type === 'other'
          ? item.type
          : 'other'; /* default/fallback */

      const title = typeof item.title === 'string' ? item.title : '';
      const path = typeof item.path === 'string' ? item.path : '';
      const header = typeof item.header === 'string' ? item.header : title;

      const apiId = typeof item.id === 'string' && item.id.trim() ? item.id : undefined;
      const debugIdRaw = item.debug?.id;
      const debugId =
        typeof debugIdRaw === 'string' && debugIdRaw.trim() ? debugIdRaw : typeof debugIdRaw === 'number' ? String(debugIdRaw) : undefined;

      const id = apiId ?? debugId ?? this.buildStableId({ type, path, title });

      const subtitle = sanitizeSubtitle(item.subtitle);
      const properties = sanitizeResultProperties(item.properties);
      const score = typeof item.score === 'number' && Number.isFinite(item.score) ? item.score : 0;

      return {
        id,
        header,
        title,
        type,
        score,
        subtitle,
        properties,
        path,
      };
    });

    return mapped.sort((a, b) => b.score - a.score);
  }

  private async fetchResults(text: string) {
    if (!this.apiEndpoint) {
      this.results = [];
      this.isLoading = false;

      return;
    }

    if (this.fetchAbort) {
      try {
        this.fetchAbort.abort();
      } catch {}
    }

    const ctrl = new AbortController();
    this.fetchAbort = ctrl;

    try {
      const url = this.buildQueryUrl(text);
      const res = await fetch(url, {
        signal: ctrl.signal,
        headers: { Authorization: `Bearer ${this.sessionService.token}` },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      this.results = this.toResults(data);
      this.refreshCompactScrollStateSoon();

      if (this.showAll) {
        this.expandedVisibleCount = this.expandedPageSize;
      }
    } catch (err) {
      if ((err as any)?.name !== 'AbortError') {
        this.results = [];
        try {
          console.error('GlobalSearch fetch error:', err);
        } catch {}
      }
    } finally {
      if (this.fetchAbort === ctrl) this.fetchAbort = undefined;
      this.isLoading = false;
    }
  }
}
